
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AppSetting } from '../../../../models/app-setting.models';
import { AppSettingService } from '../../../../services/app-setting.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { describeApiError } from '../../../../utils/api-error.utils';

export interface AppSettingEditDialogData {
  setting: AppSetting;
}

@Component({
  selector: 'app-app-setting-edit-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule
],
  templateUrl: './app-setting-edit-dialog.component.html',
  styleUrls: ['./app-setting-edit-dialog.component.scss'],
})
export class AppSettingEditDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AppSettingEditDialogComponent>>(MatDialogRef);
  data = inject<AppSettingEditDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private appSettingService = inject(AppSettingService);
  private snackbarService = inject(SnackbarService);

  settingForm!: FormGroup;
  isSaving = false;

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    const setting = this.data.setting;

    this.settingForm = this.fb.group({
      value: [setting.value, [Validators.required, this.getDataTypeValidator()]],
      isActive: [setting.isActive ?? true],
    });
  }

  private getDataTypeValidator() {
    const dataType = this.data.setting.dataType.toLowerCase();

    return (control: any) => {
      const value = control.value;
      if (!value) return null;

      switch (dataType) {
        case 'int':
          return /^-?\d+$/.test(value) ? null : { invalidInt: true };
        case 'bool':
          return /^(true|false|1|0)$/i.test(value) ? null : { invalidBool: true };
        case 'url':
          try {
            const url = new URL(value);
            return url.protocol === 'http:' || url.protocol === 'https:'
              ? null
              : { invalidUrl: true };
          } catch {
            return { invalidUrl: true };
          }
        case 'json':
          try {
            JSON.parse(value);
            return null;
          } catch {
            return { invalidJson: true };
          }
        default:
          return null;
      }
    };
  }

  getErrorMessage(): string {
    const valueControl = this.settingForm.get('value');
    if (valueControl?.hasError('required')) {
      return 'Value is required';
    }
    if (valueControl?.hasError('invalidInt')) {
      return 'Value must be a valid integer';
    }
    if (valueControl?.hasError('invalidBool')) {
      return 'Value must be true or false';
    }
    if (valueControl?.hasError('invalidUrl')) {
      return 'Value must be a valid HTTP or HTTPS URL';
    }
    if (valueControl?.hasError('invalidJson')) {
      return 'Value must be valid JSON';
    }
    return '';
  }

  onSave(): void {
    if (this.settingForm.invalid || this.isSaving) {
      return;
    }

    this.isSaving = true;

    const updatedSetting: AppSetting = {
      ...this.data.setting,
      value: this.settingForm.value.value,
      isActive: this.settingForm.value.isActive,
    };

    this.appSettingService.upsertSetting(updatedSetting).subscribe({
      next: (savedSetting) => {
        this.snackbarService.showSuccess('App setting updated successfully');
        this.dialogRef.close(savedSetting);
      },
      error: (error) => {
        console.error('Failed to save app setting:', error);
        this.snackbarService.showError(
          describeApiError(error, 'Failed to save app setting'),
        );
        this.isSaving = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
